import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getArticleBreadcrumb } from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70">
              <i className="ri-file-search-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-600 mb-4">请检查文章链接是否正确</p>
            <Link
              to="/"
              className="inline-flex px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const categoryName = category?.name || article.category;
  const breadcrumbItems = getArticleBreadcrumb(article.category, article.slug);

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: {
          '@type': 'Organization',
          name: article.author,
        },
        datePublished: article.publishDate,
        dateModified: article.publishDate,
        url: `https://www.guifu2-analysis.com/article/${article.slug}`,
        publisher: {
          '@type': 'Organization',
          name: '鬼父2解析站',
        },
        inLanguage: 'zh-CN',
        articleSection: categoryName,
        keywords: article.tags.join(','),
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: `鬼父2${categoryName}的核心看点是什么？`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: article.excerpt,
            },
          },
          {
            '@type': 'Question',
            name: `如何全面了解鬼父2的${categoryName}？`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `阅读鬼父2解析站的专业分析文章《${article.title}》，从多个维度深度解析相关内容。`,
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-2">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <article className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
          <div className="max-w-[860px] mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-800 hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  {categoryName}
                </Link>
                {article.hot && (
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                    热门
                  </span>
                )}
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                <span className="flex items-center gap-1.5">
                  <div className="w-6 h-6 rounded-full bg-secondary-200 flex items-center justify-center">
                    <i className="ri-user-line text-xs text-secondary-600"></i>
                  </div>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i> {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i> {article.readTime}
                </span>
              </div>

              <div className="rounded-xl overflow-hidden mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-[400px] md:h-[480px] object-cover"
                />
              </div>
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 font-medium">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-md bg-secondary-100 text-secondary-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-14 pt-10 border-t border-background-200/70">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer border border-background-200/60 hover:border-primary-200 transition-all duration-300"
                    >
                      <div className="h-[160px] overflow-hidden">
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {related.title}
                        </h4>
                        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
                          <span>{related.publishDate}</span>
                          <span>{related.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}